import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import { useDispatch, useSelector } from "react-redux";
import { Button, TextInput, Dropdown, Image } from "hesus-ui-kit";
import * as moment from "moment";
import DatePicker from "react-multi-date-picker";
import { addDepartment } from "../../../../../shared/shared.slice";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";

import Department from "../Department/Department";
import { loadSingleCompany as addSingleCompany } from "../../../CompanyList/CompanyList.slice";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "../../../../../utils/extra-style";

import "../../../../../utils/extra-style.scss";

import {
  ButtonWrapper,
  FormBody,
  SectionContainer,
  OuterWrapper,
  FlexRow,
  GridRow,
  OuterWrapperBackground,
  PaddingDiv,
  ContainerLabel,
  ParentDatePicker,
  TextInputLabel,
  UnitText,
} from "./Haulier.styled";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import "../../AddEditCompany.scss";

const Haulier = (props) => {
  const { t, id, selectedCompany, setSelectedCompany, isEditMode, leadId } =
    props;

  const history = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const generalParams = useGetGeneralParameters();
  const [isHaulierCreating, setIsHaulierCreating] = useState(false);
  const [createHaulierAPIError, setCreateHaulierAPIError] = useState("");

  const { departmentList } = generalParams;

  const countryId = selectedCompany?.platformCountryId;

  // get general parameters
  const fetchedCountryRef = useRef(null);
  const fetchCountryDepartment = () => {
    axios
      .get(`/general/countryDepartments?countryId=${countryId}`)
      .then((response) => {
        dispatch(addDepartment(response.data));
      })
      .catch((error) => {});
  };

  useEffect(() => {
    if (countryId && fetchedCountryRef.current !== countryId) {
      fetchedCountryRef.current = countryId;
      fetchCountryDepartment();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId]);

  let companyId = parseInt(params.id, 10);

  if (selectedCompany?.id) companyId = selectedCompany.id;

  const [formData, setFormData] = useState({
    logisticFlow: {
      required: true,
      value: selectedCompany?.haulier?.logisticFlow ?? "",
      error: "",
    },
    unloadingDepartments: {
      error: "",
      value: selectedCompany?.haulier?.unloadingDepartments ?? "",
      required: false,
    },
    loadingDepartments: {
      error: "",
      value: selectedCompany?.haulier?.loadingDepartments ?? "",
      required: false,
    },
    approvalType: {
      required: true,
      value: selectedCompany?.haulier?.approvalType ?? [],
      error: "",
    },
    approvalNumber: {
      required: true,
      value: selectedCompany?.haulier?.approvalNumber ?? "",
      error: "",
    },
    approvalEndDate: {
      required: true,
      value: selectedCompany?.haulier?.approvalEndDate ?? "",
      error: "",
    },
    maxDistancePerDay: {
      required: true,
      value: selectedCompany?.haulier?.maxDistancePerDay ?? "",
      error: "",
    },
    maxTimePerDay: {
      required: true,
      value: selectedCompany?.haulier?.maxTimePerDay ?? "",
      error: "",
    },
    trucksToOrder: {
      required: true,
      value: selectedCompany?.haulier?.trucksToOrder ?? "",
      error: "",
    },
  });

  const onApprovalDateChange = (data) => {
    setFormData({
      ...formData,
      approvalEndDate: {
        value: data,
        error: "",
        required: true,
      },
    });
  };

  const onLogisticFlowChange = (logisticFlow) => {
    setFormData({
      ...formData,
      logisticFlow: {
        ...formData.logisticFlow,
        error: "",
        value: logisticFlow.value ? parseInt(logisticFlow.value, 10) : "",
      },
    });
  };

  const onChangeApprovalType = (approvalId, label) => {
    if (
      formData.approvalType.value.find(
        (obj) => parseInt(obj.id, 10) === approvalId
      )
    ) {
      const filteredTypes = formData.approvalType.value.filter(
        (item) => parseInt(item.id, 10) !== approvalId
      );
      setFormData({
        ...formData,
        approvalType: {
          ...formData.approvalType,
          error: "",
          value: filteredTypes,
        },
      });
    } else {
      setFormData({
        ...formData,
        approvalType: {
          ...formData.approvalType,
          error: "",
          value: [...formData.approvalType.value, { id: approvalId, label }],
        },
      });
    }
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.logisticFlow.value) {
      isFormValid = false;
      data.logisticFlow.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (
      data.trucksToOrder.value &&
      !/^[0-9\b]+$/i.test(data.trucksToOrder.value)
    ) {
      isFormValid = false;
      data.trucksToOrder.error = t("INVALID_FORMAT");
    }
    if (
      data.maxDistancePerDay.value &&
      !/^[0-9\b]+$/i.test(data.maxDistancePerDay.value)
    ) {
      isFormValid = false;
      data.maxDistancePerDay.error = t("INVALID_FORMAT");
    }
    if (
      data.maxTimePerDay.value &&
      !/^[0-9\b]+$/i.test(data.maxTimePerDay.value)
    ) {
      isFormValid = false;
      data.maxTimePerDay.error = t("INVALID_FORMAT");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };

  const getFormData = () => {
    const data = {
      id: isEditMode ? selectedCompany?.haulier?.id : "",
      logisticFlow: formData.logisticFlow.value,
      approvalType: formData.approvalType.value,
      approvalNumber: formData.approvalNumber.value,
      approvalEndDate: formData.approvalEndDate.value,
      maxDistancePerDay: formData.maxDistancePerDay.value,
      maxTimePerDay: formData.maxTimePerDay.value,
      trucksToOrder: formData.trucksToOrder.value,
      loadingDepartments: formData.loadingDepartments.value,
      unloadingDepartments: formData.unloadingDepartments.value,
      isUpdate: true,
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      const haulierData = { ...selectedCompany.haulier, ...getFormData() };
      const companyData = { ...selectedCompany, haulier: { ...haulierData } };
      setIsHaulierCreating(true);
      setCreateHaulierAPIError("");
      axios
        .put(`/companies/${companyId}`, companyData)
        .then((response) => {
          setIsHaulierCreating(false);
          setSelectedCompany(response?.data[0]);
          dispatch(addSingleCompany(response.data));
          if (leadId) history(`/lead/view/${leadId}`);
          else history(`/company/view/${response?.data[0].id}/general-info`);
        })
        .catch((error) => {
          setIsHaulierCreating(false);
          setCreateHaulierAPIError(error.response?.data?.message ?? "");
        });
    }
  };

  const onCancel = () => {
    if (leadId) history(`/lead/view/${leadId}`);
    else history(`/company/view/${companyId}/general-info`);
  };

  const setUnloadingDepartmentData = (value) => {
    setFormData({
      ...formData,
      unloadingDepartments: {
        value,
        error: "",
        required: true,
      },
    });
  };

  const setLoadingDepartmentData = (value) => {
    setFormData({
      ...formData,
      loadingDepartments: {
        value,
        error: "",
        required: true,
      },
    });
  };

  return (
    <OuterWrapperBackground>
      <PaddingDiv>
        <FormBody>
          <OuterWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr 1fr">
                    <Dropdown
                      id="centre-logistic-flow"
                      label={t("LOGISTIC_FLOW")}
                      name="centre-logistic-flow"
                      value={formData.logisticFlow.value}
                      defaultOption={t("NONE_SELECTED")}
                      options={[
                        { label: t("LOGISTIC_FLOW_ROUND_TRIP"), value: 1 },
                        { label: t("LOGISTIC_FLOW_RETURN_LOAD"), value: 2 },
                        { label: t("LOGISTIC_FLOW_BOTH"), value: 3 },
                      ]}
                      required
                      containerStyles={{ marginBottom: "1.32rem" }}
                      onChange={onLogisticFlowChange}
                      labelStyles={
                        formData.logisticFlow.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.logisticFlow.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.logisticFlow.error}
                    />

                    <div>
                      <ContainerLabel
                        htmlFor="approval_type"
                        labelStyles={labelStyles}
                      >
                        {t("HAULIER_APPROVAL_TYPE")}
                      </ContainerLabel>
                      <GridRow column="1fr 1fr 1fr">
                        <div>
                          <Image
                            src={
                              formData.approvalType.value.find(
                                (obj) => parseInt(obj.id, 10) === 1
                              )
                                ? enableIcon
                                : disableIcon
                            }
                            alt={t("WASTE_DANGERIOUS")}
                            title={t("WASTE_DANGERIOUS")}
                            styles={{ marginRight: "10px" }}
                            onClick={() =>
                              onChangeApprovalType(1, "WASTE_DANGERIOUS")
                            }
                          />
                          <label className="label center-products-list__active-label">
                            {t("WASTE_DANGERIOUS")}
                          </label>
                        </div>

                        <div>
                          <Image
                            src={
                              formData.approvalType.value.find(
                                (obj) => parseInt(obj.id, 10) === 2
                              )
                                ? enableIcon
                                : disableIcon
                            }
                            alt={t("NON_DANGEROUS_WASTE")}
                            title={t("NON_DANGEROUS_WASTE")}
                            styles={{ marginRight: "10px" }}
                            onClick={() =>
                              onChangeApprovalType(2, "NON_DANGEROUS_WASTE")
                            }
                          />
                          <label className="label center-products-list__active-label">
                            {t("NON_DANGEROUS_WASTE")}
                          </label>
                        </div>

                        <div>
                          <Image
                            src={
                              formData.approvalType.value.find(
                                (obj) => parseInt(obj.id, 10) === 3
                              )
                                ? enableIcon
                                : disableIcon
                            }
                            alt={t("SOIL_NONPOLLUTED")}
                            title={t("SOIL_NONPOLLUTED")}
                            styles={{ marginRight: "10px" }}
                            onClick={() =>
                              onChangeApprovalType(3, "SOIL_NONPOLLUTED")
                            }
                          />
                          <label className="label center-products-list__active-label">
                            {t("SOIL_NONPOLLUTED")}
                          </label>
                        </div>
                      </GridRow>
                    </div>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <TextInput
                      type="text"
                      label={t("HAULIER_APPROVAL_NUMBER")}
                      name="centre-simultaneous-loads"
                      id="centre-simultaneous-loads"
                      value={formData.approvalNumber?.value || ""}
                      isFloat
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          approvalNumber: {
                            value: e.target.value,
                            error: "",
                            required: true,
                          },
                        });
                      }}
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.approvalNumber.error}
                      placeholder={t("HAULIER_APPROVAL_NUMBER")}
                    />
                    <ParentDatePicker>
                      <TextInputLabel>
                        {" "}
                        {t("HAULIER_APPROVAL_END_DATE")}
                      </TextInputLabel>
                      <DatePicker
                        style={{
                          marginTop: "24px",
                          padding: "5px 10px",
                          backgroundColor: "white",
                          borderColor: "hsl(0deg, 0%, 80%)",
                          borderRadius: "4px",
                          fontFamily: "Lato",
                          fontSize: "16px",
                          height: "auto",
                          width: "100%",
                          lineHeight: "1.4",
                          border: "1px solid #474747",
                        }}
                        title={t("HAULIER_APPROVAL_END_DATE")}
                        className={{ ParentDatePicker }}
                        containerClassName={{ ParentDatePicker }}
                        calendarPosition="bottom-center"
                        onChange={(e) => {
                          onApprovalDateChange(
                            moment(e.toDate()).format("YYYY-MM-DD")
                          );
                        }}
                        format={getDatePickerFormat()}
                        value={
                          getDateFormat(
                            formData.approvalEndDate.value,
                            "YYYY-MM-DD"
                          ) || ""
                        }
                        locale={getCalendarLocale()}
                        name="meeting-date"
                      />
                    </ParentDatePicker>
                  </GridRow>
                  <GridRow column="1fr 6rem 1fr 3rem 1fr" gap=".25rem">
                    <TextInput
                      type="text"
                      label={t("HAULIER_DISTANCE_LIMIT")}
                      name="centre-simultaneous-loads"
                      id="centre-simultaneous-loads"
                      value={formData.maxDistancePerDay?.value || ""}
                      isFloat
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          maxDistancePerDay: {
                            value: e.target.value,
                            error: "",
                            required: true,
                          },
                        });
                      }}
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.maxDistancePerDay.error}
                      placeholder={t("HAULIER_DISTANCE_LIMIT")}
                    />
                    <div>
                      {formData.maxDistancePerDay.error && (
                        <UnitText>{t("GENERAL_UNIT_KM")}</UnitText>
                      )}
                      {!formData.maxDistancePerDay.error && (
                        <UnitText>{t("GENERAL_UNIT_KM")}</UnitText>
                      )}
                    </div>

                    <TextInput
                      type="text"
                      label={t("HAULIER_DRIVING_TIME_LIMIT")}
                      name="centre-simultaneous-loads"
                      id="centre-simultaneous-loads"
                      value={formData.maxTimePerDay?.value || ""}
                      isFloat
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          maxTimePerDay: {
                            value: e.target.value,
                            error: "",
                            required: true,
                          },
                        });
                      }}
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.maxTimePerDay.error}
                      placeholder={t("HAULIER_DRIVING_TIME_LIMIT")}
                    />
                    <div>
                      {formData.maxTimePerDay.error && (
                        <UnitText>{t("MINUTES_ABREVIATED")}</UnitText>
                      )}
                      {!formData.maxTimePerDay.error && (
                        <UnitText>{t("MINUTES_ABREVIATED")}</UnitText>
                      )}
                    </div>
                    <TextInput
                      type="text"
                      label={t("HAULIER_TRUCKS_TO_ORDER")}
                      name="centre-simultaneous-loads"
                      id="centre-simultaneous-loads"
                      value={formData.trucksToOrder?.value || ""}
                      isFloat
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          trucksToOrder: {
                            value: e.target.value,
                            error: "",
                            required: true,
                          },
                        });
                      }}
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.trucksToOrder.error}
                      placeholder={t("HAULIER_TRUCKS_TO_ORDER")}
                    />
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <div>
                      <div>
                        <ContainerLabel
                          htmlFor="access"
                          labelStyles={labelStyles}
                        >
                          {t("HAULIER_LOADING_DEPARTMENTS")}
                        </ContainerLabel>
                      </div>

                      <Department
                        selectedDepartment={
                          formData.loadingDepartments.value !== ""
                            ? formData.loadingDepartments.value.split(",")
                            : []
                        }
                        error={formData.loadingDepartments.error}
                        setDepartmentValue={setLoadingDepartmentData}
                        type="loading"
                      />
                    </div>
                    <div>
                      <div>
                        <ContainerLabel
                          htmlFor="access"
                          labelStyles={labelStyles}
                        >
                          {t("HAULIER_UNLOADING_DEPARTMENTS")}
                        </ContainerLabel>
                      </div>
                      <Department
                        selectedDepartment={
                          formData.unloadingDepartments.value !== ""
                            ? formData.unloadingDepartments.value.split(",")
                            : []
                        }
                        error={formData.unloadingDepartments.error}
                        setDepartmentValue={setUnloadingDepartmentData}
                        type="unloading"
                      />
                    </div>
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OuterWrapper>
        </FormBody>
        <ButtonWrapper>
          {createHaulierAPIError && <div>{createHaulierAPIError}</div>}
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onCancel}
          >
            <>{t("CANCEL")}</>
          </Button>

          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isHaulierCreating && <ClipLoader size={15} color="#fff" />}
              {!isHaulierCreating && t("SUBMIT")}
            </>
          </Button>
        </ButtonWrapper>
      </PaddingDiv>
    </OuterWrapperBackground>
  );
};
Haulier.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  isEditMode: PropTypes.bool,
};
Haulier.defaultProps = {
  id: null,
  isEditMode: false,
};

export default withTranslation()(Haulier);
